import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';
import LossReasonModal from './LossReasonModal';
import NegotiationBriefPanel from './NegotiationBriefPanel';

// Visual styling per stage — matches the color scheme on Home.jsx pipeline.
const DEAL_STAGES = [
  { key: 'identified',     label: 'Identified',     cls: 'bg-gray-600/30 text-gray-200 border-gray-500/40' },
  { key: 'touring',        label: 'Touring',        cls: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
  { key: 'negotiating',    label: 'Negotiating',    cls: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40' },
  { key: 'loi',            label: 'LOI',            cls: 'bg-orange-500/20 text-orange-300 border-orange-500/40' },
  { key: 'under_contract', label: 'Under Contract', cls: 'bg-[#00D4FF]/20 text-[#00D4FF] border-[#00D4FF]/40' },
  { key: 'closed',         label: 'Closed',         cls: 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40' },
  { key: 'lost',           label: 'Lost',           cls: 'bg-red-500/20 text-red-300 border-red-500/40' },
];

function DealStageCard({ currentStage, saving, toast, onChange }) {
  const current = DEAL_STAGES.find(s => s.key === currentStage) || DEAL_STAGES[0];
  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#FF6B35] p-5">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-gray-400 text-xs uppercase tracking-wider">Deal Stage</span>
          <span className={`px-3 py-1 rounded text-xs font-bold border ${current.cls}`}>
            {current.label}
          </span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <select
            value={currentStage}
            onChange={(e) => onChange(e.target.value)}
            disabled={saving}
            className="bg-[#0A0E27] border border-[#546E7A]/50 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#00D4FF] disabled:opacity-50"
          >
            {DEAL_STAGES.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          {saving && <span className="text-gray-500 text-xs">Saving…</span>}
        </div>
      </div>
      {toast && (
        <div className={`mt-3 text-sm ${toast.startsWith('✓') ? 'text-[#00E676]' : 'text-red-300'}`}>
          {toast}
        </div>
      )}
    </div>
  );
}

// Friendly labels for the loss_reason taxonomy — must stay in sync with backend.
const LOSS_REASON_LABEL = {
  pricing:         'Pricing',
  competition:     'Competition',
  timing:          'Timing',
  property_issues: 'Property Issues',
  financing:       'Financing',
  needs_changed:   'Needs Changed',
  location:        'Location',
  terms:           'Terms',
  other:           'Other',
};

function LossDetailsCard({ fitScore, onEdit }) {
  const reasonKey = fitScore?.loss_reason;
  const reasonLabel = reasonKey ? (LOSS_REASON_LABEL[reasonKey] || reasonKey) : null;
  const notes = fitScore?.loss_notes;
  const recordedAt = fitScore?.loss_recorded_at;
  const recordedDate = recordedAt ? String(recordedAt).split(' ')[0] : null;

  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-red-500 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-500"></span>
          Loss Details
        </h3>
        <button
          onClick={onEdit}
          className="px-3 py-1 text-xs font-semibold rounded-lg bg-[#243B55] text-[#00D4FF] hover:bg-[#2d4a6a] transition-colors"
        >
          {reasonKey ? 'Edit' : 'Add details'}
        </button>
      </div>

      {!reasonKey ? (
        <p className="text-gray-500 text-sm italic">No loss details recorded yet.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-gray-500 text-xs uppercase tracking-wider">Reason</span>
            <span className="px-2 py-0.5 rounded text-xs font-bold border bg-red-500/15 text-red-300 border-red-500/40 uppercase tracking-wide">
              {reasonLabel}
            </span>
            {recordedDate && (
              <span className="text-gray-500 text-xs ml-auto">Recorded {recordedDate}</span>
            )}
          </div>
          {notes && (
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Notes</div>
              <p className="text-gray-200 text-sm whitespace-pre-wrap leading-relaxed">{notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PropertyEvaluation() {
  const { propertyId, buyerId } = useParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overall');
  const [property, setProperty] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [fitScore, setFitScore] = useState(null);
  const [grades, setGrades] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [progress, setProgress] = useState(null);
  const [buyerTargets, setBuyerTargets] = useState([]);
  const [aiReport, setAiReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingStage, setSavingStage] = useState(false);
  const [stageToast, setStageToast] = useState('');
  const [pendingLossStage, setPendingLossStage] = useState(null);
  const [editingLoss, setEditingLoss] = useState(false);
  const [showNegotiation, setShowNegotiation] = useState(false);

  // Collapsible sections on the Overall tab. All start expanded so the full dashboard
  // is visible immediately; users can collapse individually or via the Collapse All link.
  // The Property Profile Card (Row 1) is always visible and not in this state.
  const [sectionsOpen, setSectionsOpen] = useState({
    keyMetrics: true,
    categoryScores: true,
    weightDist: true,
    topStrengths: true,
    areasConcern: true,
  });
  const toggleSection = (key) => setSectionsOpen(s => ({ ...s, [key]: !s[key] }));
  const allExpanded = Object.values(sectionsOpen).every(Boolean);
  const setAllSections = (open) => setSectionsOpen({
    keyMetrics: open,
    categoryScores: open,
    weightDist: open,
    topStrengths: open,
    areasConcern: open,
  });

  // Collapsible parent categories on the Grading tab. Keyed by parent.id.
  // Missing key means collapsed (the default for any unknown parent).
  const [gradingSectionsOpen, setGradingSectionsOpen] = useState({});
  const toggleGradingSection = (id) =>
    setGradingSectionsOpen(s => ({ ...s, [id]: !s[id] }));
  const setAllGradingSections = (open) => {
    const next = {};
    hierarchy.forEach(p => { next[p.id] = open; });
    setGradingSectionsOpen(next);
  };
  const allGradingExpanded =
    hierarchy.length > 0 && hierarchy.every(p => !!gradingSectionsOpen[p.id]);

  useEffect(() => { loadData(); }, [propertyId, buyerId]);

  const loadData = async () => {
    try {
      const [propRes, buyerRes, fitRes, gradesRes, hierRes, progressRes, overridesRes, notesRes, targetsRes] = await Promise.all([
        authFetch(`${API}/api/properties/${propertyId}`),
        authFetch(`${API}/api/buyers/${buyerId}`),
        authFetch(`${API}/api/grades/fit-score/${propertyId}/buyer/${buyerId}`).catch(() => null),
        authFetch(`${API}/api/grades/property/${propertyId}/buyer/${buyerId}`),
        authFetch(`${API}/api/criteria/buyer/${buyerId}/hierarchy`),
        authFetch(`${API}/api/grades/progress/${propertyId}/buyer/${buyerId}`),
        authFetch(`${API}/api/overrides/${propertyId}/buyer/${buyerId}`).catch(() => null),
        authFetch(`${API}/api/notes/${propertyId}/buyer/${buyerId}`).catch(() => null),
        authFetch(`${API}/api/measurables/definitions/${buyerId}`).catch(() => null),
      ]);
      if (propRes.ok) setProperty(await propRes.json());
      if (buyerRes.ok) setBuyer(await buyerRes.json());
      if (fitRes && fitRes.ok) setFitScore(await fitRes.json());
      if (gradesRes.ok) setGrades(await gradesRes.json());
      if (hierRes.ok) setHierarchy(await hierRes.json());
      if (progressRes.ok) setProgress(await progressRes.json());
      if (overridesRes && overridesRes.ok) {
        const oData = await overridesRes.json();
        const oMap = {};
        oData.forEach(o => { oMap[o.criterion_id] = o; });
        setOverrides(oMap);
      }
      if (notesRes && notesRes.ok) setNotes(await notesRes.json());
      if (targetsRes && targetsRes.ok) setBuyerTargets(await targetsRes.json());
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const persistStage = async (newStage, extra = {}) => {
    setSavingStage(true);
    setStageToast('');
    try {
      const res = await authFetch(`${API}/api/deals/${propertyId}/buyer/${buyerId}/stage`, {
        method: 'PATCH',
        body: JSON.stringify({ stage: newStage, ...extra }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Update failed (${res.status})`);
      }
      const data = await res.json();
      setFitScore((prev) => prev ? { ...prev, deal_stage: data.stage } : { deal_stage: data.stage });
      setStageToast(`✓ Stage updated to ${data.stage_label}`);
      setTimeout(() => setStageToast(''), 2500);
    } catch (e) {
      setStageToast(`⚠ ${e.message}`);
      setTimeout(() => setStageToast(''), 3500);
      throw e;
    } finally {
      setSavingStage(false);
    }
  };

  const handleStageChange = async (newStage) => {
    // Intercept "lost" — the modal collects reason+notes before we persist
    if (newStage === 'lost') {
      setPendingLossStage(newStage);
      return;
    }
    await persistStage(newStage).catch(() => {});
  };

  const handleGrade = async (criterionId, grade) => {
    try {
      await authFetch(`${API}/api/grades`, {
        method: 'POST',
        body: JSON.stringify({
          property_id: parseInt(propertyId),
          buyer_id: parseInt(buyerId),
          criterion_id: criterionId,
          grade: grade,
        })
      });
      loadData();
    } catch (err) {
      console.error('Grade error:', err);
    }
  };

  const handleOverride = async (criterionId, grade) => {
    try {
      await authFetch(`${API}/api/overrides`, {
        method: 'POST',
        body: JSON.stringify({
          property_id: parseInt(propertyId),
          buyer_id: parseInt(buyerId),
          criterion_id: criterionId,
          grade: grade,
        })
      });
      loadData();
    } catch (err) {
      console.error('Override error:', err);
    }
  };

  const removeOverride = async (criterionId) => {
    try {
      await authFetch(`${API}/api/overrides/${propertyId}/buyer/${buyerId}/criterion/${criterionId}`, {
        method: 'DELETE'
      });
      loadData();
    } catch (err) {
      console.error('Remove override error:', err);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const res = await authFetch(`${API}/api/notes`, {
        method: 'POST',
        body: JSON.stringify({
          property_id: parseInt(propertyId),
          buyer_id: parseInt(buyerId),
          note_text: newNote.trim(),
        })
      });
      if (res.ok) {
        setNewNote('');
        loadData();
      }
    } catch (err) {
      console.error('Add note error:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await authFetch(`${API}/api/notes/${noteId}`, { method: 'DELETE' });
      loadData();
    } catch (err) {
      console.error('Delete note error:', err);
    }
  };

  const generateAiReport = async () => {
    setGeneratingReport(true);
    try {
      const res = await authFetch(`${API}/api/ai/${propertyId}/buyer/${buyerId}/report`);
      if (res.ok) {
        const data = await res.json();
        setAiReport(data.report);
      }
    } catch (err) {
      console.error('AI report error:', err);
    } finally {
      setGeneratingReport(false);
    }
  };

  // ─── Helpers ───

  const getTierColor = (tier) => ({
    'ELITE FIT': 'text-[#00E676]',
    'STRONG FIT': 'text-[#00D4FF]',
    'GOOD FIT': 'text-yellow-400',
    'MARGINAL FIT': 'text-orange-400',
    'POOR FIT': 'text-red-400',
  }[tier] || 'text-gray-400');

  const getGradeColor = (grade) => {
    if (grade >= 8) return 'bg-green-600';
    if (grade >= 7) return 'bg-cyan-600';
    if (grade >= 6) return 'bg-blue-600';
    if (grade >= 4) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  // Solid hex variants used for inline SVG / bar fills where a Tailwind class isn't usable.
  const scoreHex = (score) => {
    if (score >= 7) return '#00E676';
    if (score >= 5) return '#FFC107';
    if (score >= 3) return '#FF6B35';
    return '#F44336';
  };
  const scoreBarBg = (score) => {
    if (score >= 7) return 'bg-[#00E676]';
    if (score >= 5) return 'bg-yellow-400';
    if (score >= 3) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const listingTypeBadge = (lt) => {
    if (lt === 'for_sale')           return { label: 'FOR SALE',     cls: 'bg-green-900/50 text-green-300 border-green-600/40' };
    if (lt === 'for_sale_and_lease') return { label: 'SALE & LEASE', cls: 'bg-orange-900/50 text-orange-300 border-orange-600/40' };
    if (lt === 'for_lease')          return { label: 'FOR LEASE',    cls: 'bg-blue-900/50 text-blue-300 border-blue-600/40' };
    return null;
  };

  const formatPrice = (price) => price ? `$${Number(price).toLocaleString()}` : 'N/A';
  const getScorePercent = (score) => score ? Math.round((score / 9) * 100) : 0;
  const fmtPct = (n) => `${n.toFixed(1)}%`;

  const getExistingGrade = (criterionId) => {
    const g = grades.find(g => g.criterion_id === criterionId);
    return g ? g.grade : null;
  };

  // ─── Pie chart math ───
  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const a = (angleDeg - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  };
  const arcPath = (cx, cy, r, startAngle, endAngle) => {
    if (endAngle - startAngle >= 359.999) {
      // Full circle — draw two half arcs to avoid degenerate path.
      return `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} Z`;
    }
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const large = endAngle - startAngle <= 180 ? 0 : 1;
    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y} Z`;
  };

  // ─── Key Metrics: derive from property + buyer targets ───
  const findTarget = (...needles) => {
    for (const def of buyerTargets) {
      const n = (def.name || '').toLowerCase();
      if (needles.some(needle => n.includes(needle))) return def;
    }
    return null;
  };

  const buildMetrics = () => {
    if (!property) return [];
    const m = [];
    // Lease Rate ($/sqft/yr) — lower is better
    if (property.lease_rate_sqft != null) {
      const t = findTarget('lease rate', 'rent');
      m.push({
        label: 'Lease Rate',
        value: `$${Number(property.lease_rate_sqft).toFixed(2)}/sqft/yr`,
        raw: parseFloat(property.lease_rate_sqft),
        target: t,
        lowerIsBetter: true,
        targetLabel: t && t.target_value ? `${t.target_operator || '<='} $${Number(t.target_value).toFixed(2)}` : null,
      });
    }
    // Square Footage — higher is better (closer-to-target preferred)
    const sf = property.rentable_sqft || property.sqft;
    if (sf != null) {
      const t = findTarget('sqft', 'square foot');
      m.push({
        label: 'Square Footage',
        value: `${Number(sf).toLocaleString()} sqft`,
        raw: parseFloat(sf),
        target: t,
        lowerIsBetter: false,
        targetLabel: t && t.target_value ? `>= ${Number(t.target_value).toLocaleString()}` : null,
      });
    }
    // Ceiling Height — higher is better
    if (property.ceiling_height_ft != null) {
      const t = findTarget('ceiling');
      m.push({
        label: 'Ceiling Height',
        value: `${Number(property.ceiling_height_ft).toFixed(1)} ft`,
        raw: parseFloat(property.ceiling_height_ft),
        target: t,
        lowerIsBetter: false,
        targetLabel: t && t.target_value ? `>= ${Number(t.target_value).toFixed(1)} ft` : null,
      });
    }
    // Parking Ratio — higher is better
    if (property.parking_ratio != null) {
      const t = findTarget('parking');
      m.push({
        label: 'Parking Ratio',
        value: `${Number(property.parking_ratio).toFixed(1)} /1000 sqft`,
        raw: parseFloat(property.parking_ratio),
        target: t,
        lowerIsBetter: false,
        targetLabel: t && t.target_value ? `>= ${Number(t.target_value).toFixed(1)}` : null,
      });
    }
    // Cap Rate — higher is better (for buyers/investors)
    if (property.cap_rate != null) {
      const t = findTarget('cap rate');
      m.push({
        label: 'Cap Rate',
        value: `${Number(property.cap_rate).toFixed(2)}%`,
        raw: parseFloat(property.cap_rate),
        target: t,
        lowerIsBetter: false,
        targetLabel: t && t.target_value ? `>= ${Number(t.target_value).toFixed(2)}%` : 'Market: 6–8%',
      });
    }
    return m;
  };

  const metricStatus = (m) => {
    if (!m.target || m.target.target_value == null) {
      // No buyer target — give a neutral half-fill bar
      return { color: 'bg-gray-500', pct: 50, hex: '#6B7280' };
    }
    const target = parseFloat(m.target.target_value);
    if (m.lowerIsBetter) {
      if (m.raw <= target) {
        return { color: 'bg-[#00E676]', pct: Math.min(100, Math.max(35, (target / Math.max(m.raw, 0.01)) * 60)), hex: '#00E676' };
      }
      const overshoot = (m.raw - target) / target;
      if (overshoot <= 0.2) return { color: 'bg-yellow-400', pct: Math.max(30, 60 - overshoot * 100), hex: '#FFC107' };
      return { color: 'bg-red-500', pct: 25, hex: '#F44336' };
    } else {
      const ratio = m.raw / target;
      if (ratio >= 1.0) return { color: 'bg-[#00E676]', pct: Math.min(100, ratio * 60), hex: '#00E676' };
      if (ratio >= 0.8) return { color: 'bg-yellow-400', pct: ratio * 70, hex: '#FFC107' };
      return { color: 'bg-red-500', pct: Math.max(15, ratio * 50), hex: '#F44336' };
    }
  };

  // Flatten grades + sub-criterion names for strengths/concerns lists.
  const gradedSubs = (() => {
    const out = [];
    hierarchy.forEach(parent => {
      (parent.sub_criteria || []).forEach(sub => {
        const g = grades.find(gr => gr.criterion_id === sub.id);
        const o = overrides[sub.id];
        const score = o ? o.grade : (g ? g.grade : null);
        if (score != null) {
          out.push({ name: sub.name, score, parent: parent.name });
        }
      });
    });
    return out;
  })();
  const topStrengths = [...gradedSubs].sort((a, b) => b.score - a.score).slice(0, 4);
  const topConcerns  = [...gradedSubs].sort((a, b) => a.score - b.score).slice(0, 4);
  // True when this (property, client) pair has any real grades backing the
  // fit score. A fit_score row alone (created e.g. by a stage-only PATCH) is
  // not enough — without grades, the overall percentage is misleading.
  const hasGrades = grades.some(g => g != null && g.grade != null);

  const PARENT_COLORS = ['#00D4FF', '#00E676', '#FF6B35', '#9C27B0', '#FFC107', '#26C6DA', '#EF5350'];

  // ─── Tabs ───
  const allTabs = [
    { id: 'overall', label: 'Overall' },
    { id: 'grading', label: 'Grading' },
    { id: 'notes',   label: 'Notes' },
    { id: 'report',  label: 'AI Report' },
  ];
  const tabs = allTabs;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading evaluation...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-red-400 text-lg">Property not found</div>
      </div>
    );
  }

  // Pre-compute derived data used by the Overall tab.
  const metrics = buildMetrics();
  const measuredCount = metrics.filter(m => m.target && m.target.target_value != null).length;
  const dimEntries = fitScore && fitScore.dimension_scores
    ? Object.entries(fitScore.dimension_scores)
    : [];
  const parentWeights = hierarchy
    .filter(p => p.weight != null && p.weight > 0)
    .map((p, i) => ({ name: p.name, weight: parseFloat(p.weight), color: PARENT_COLORS[i % PARENT_COLORS.length] }));
  const weightTotal = parentWeights.reduce((s, p) => s + p.weight, 0) || 1;
  const lt = listingTypeBadge(property.listing_type);

  // Pie chart slices
  let cumDeg = 0;
  const pieSlices = parentWeights.map(p => {
    const sweep = (p.weight / weightTotal) * 360;
    const startAngle = cumDeg;
    const endAngle = cumDeg + sweep;
    const mid = (startAngle + endAngle) / 2;
    const labelPos = polarToCartesian(100, 100, 60, mid);
    cumDeg = endAngle;
    return {
      ...p,
      path: arcPath(100, 100, 90, startAngle, endAngle),
      label: `${Math.round((p.weight / weightTotal) * 100)}%`,
      labelX: labelPos.x,
      labelY: labelPos.y,
    };
  });

  // Score completion stats
  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Slim Header */}
        <div className="mb-4">
          <Link to={`/match-board?buyer=${buyerId}`} className="text-[#00D4FF] text-sm hover:text-[#00E676] transition-colors mb-1 inline-block">
            ← Back to Match Board
          </Link>
          <p className="text-[#B0BEC5] text-sm">Property Evaluation for <span className="text-[#00D4FF] font-medium">{buyer?.name}</span></p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 border-b border-[#546E7A]/30">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'text-[#00D4FF] border-[#00D4FF]'
                  : 'text-gray-400 border-transparent hover:text-white hover:border-[#546E7A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ──────────────── OVERALL TAB ──────────────── */}
        {activeTab === 'overall' && (
          <div className="space-y-5">
            {/* Expand / collapse all (sections below the profile card) */}
            <div className="flex justify-end -mb-2">
              <button
                type="button"
                onClick={() => setAllSections(!allExpanded)}
                className="text-[#00D4FF] hover:text-[#00E676] text-xs font-medium transition-colors"
              >
                {allExpanded ? 'Collapse All ▲' : 'Expand All ▼'}
              </button>
            </div>

            {/* ROW 1 — Property Profile Card */}
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00D4FF] p-5 md:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: identity + specs */}
                <div className="lg:col-span-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                    {property.property_name || property.address}
                  </h1>
                  <p className="text-gray-400 text-sm mt-1">
                    {property.address}{property.city ? ` · ${property.city}, ${property.state} ${property.zip || ''}` : ''}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {property.property_type && (
                      <span className="px-2.5 py-1 rounded text-xs font-semibold border bg-[#243B55] text-[#00D4FF] border-[#00D4FF]/30 capitalize">
                        {property.property_type.replace('_', ' ')}
                      </span>
                    )}
                    {lt && (
                      <span className={`px-2.5 py-1 rounded text-xs font-bold border ${lt.cls}`}>{lt.label}</span>
                    )}
                    {property.listing_status && (
                      <span className={`px-2.5 py-1 rounded text-xs font-medium border capitalize ${
                        property.listing_status === 'active' ? 'bg-green-900/40 text-green-300 border-green-500/30' :
                        property.listing_status === 'pending' ? 'bg-yellow-900/40 text-yellow-300 border-yellow-500/30' :
                        'bg-gray-800 text-gray-400 border-gray-600/30'
                      }`}>{property.listing_status}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                    <div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider">Sqft</div>
                      <div className="text-white font-bold text-lg">
                        {(property.rentable_sqft || property.sqft)
                          ? Number(property.rentable_sqft || property.sqft).toLocaleString()
                          : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider">Class</div>
                      <div className="text-white font-bold text-lg">{property.building_class ? `Class ${property.building_class}` : '—'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider">Year Built</div>
                      <div className="text-white font-bold text-lg">{property.year_built || '—'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider">Stories</div>
                      <div className="text-white font-bold text-lg">{property.stories || '—'}</div>
                    </div>
                  </div>
                </div>

                {/* Right: fit score + top strengths */}
                <div className="border-t lg:border-t-0 lg:border-l border-[#546E7A]/20 pt-5 lg:pt-0 lg:pl-6">
                  {!fitScore || !hasGrades ? (
                    <>
                      <div className="text-2xl md:text-3xl font-bold text-gray-400 leading-tight">
                        Not Yet Scored
                      </div>
                      <p className="text-gray-500 text-sm mt-3">
                        This property hasn't been scored against this client's criteria yet.
                        Grades will be calculated automatically.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="text-5xl md:text-6xl font-bold text-[#00D4FF] leading-none">
                        {getScorePercent(fitScore.overall)}%
                      </div>
                      <div className={`text-base font-bold mt-1 ${getTierColor(fitScore.tier)}`}>{fitScore.tier}</div>
                      <div className="mt-4">
                        <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">Top Strengths</div>
                        {(fitScore.highlights && fitScore.highlights.strengths && fitScore.highlights.strengths.length > 0) ? (
                          <div className="space-y-1">
                            {fitScore.highlights.strengths.slice(0, 3).map((s, i) => (
                              <div key={i} className="flex items-start text-sm">
                                <span className="text-[#00E676] mr-2 mt-0.5">›</span>
                                <span className="text-gray-300 flex-1">{s.name}</span>
                                <span className="text-[#00E676] font-semibold ml-2">{getScorePercent(s.score)}%</span>
                              </div>
                            ))}
                          </div>
                        ) : topStrengths.length > 0 ? (
                          <div className="space-y-1">
                            {topStrengths.slice(0, 3).map((s, i) => (
                              <div key={i} className="flex items-start text-sm">
                                <span className="text-[#00E676] mr-2 mt-0.5">›</span>
                                <span className="text-gray-300 flex-1">{s.name}</span>
                                <span className="text-[#00E676] font-semibold ml-2">{s.score.toFixed(1)}/9</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-600 text-sm">No grades yet</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ROW 1.5 — Deal Stage selector */}
            <DealStageCard
              currentStage={fitScore?.deal_stage || 'identified'}
              saving={savingStage}
              toast={stageToast}
              onChange={handleStageChange}
            />

            {/* ROW 1.55 — Negotiation Brief trigger.
                Visually prominent when the deal is in negotiating/loi (the moment
                this matters most), but always available. */}
            {(() => {
              const inNegotiation = fitScore?.deal_stage === 'negotiating' || fitScore?.deal_stage === 'loi';
              return (
                <div
                  className={`rounded-xl border p-4 flex items-center justify-between gap-3 flex-wrap transition-all ${
                    inNegotiation
                      ? 'bg-gradient-to-r from-[#FF6B35]/15 to-[#FFC107]/10 border-[#FF6B35]/40 shadow-lg shadow-[#FF6B35]/10'
                      : 'bg-[#1A2744] border-[#546E7A]/30'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-2xl shrink-0">🤝</span>
                    <div className="min-w-0">
                      <div className="text-white font-semibold text-sm">Negotiation Brief</div>
                      <div className="text-gray-400 text-xs mt-0.5">
                        {inNegotiation
                          ? 'AI-built leverage points, position, and talking points — generate before your next call.'
                          : 'Strategy brief on demand — leverage points, target/walk-away, and ready phrases.'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowNegotiation(true)}
                    className={`shrink-0 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                      inNegotiation
                        ? 'bg-[#FF6B35] text-white hover:brightness-110'
                        : 'bg-[#243B55] text-[#FF6B35] hover:bg-[#2d4a6a]'
                    }`}
                  >
                    Generate
                  </button>
                </div>
              );
            })()}

            {/* ROW 1.6 — Loss Details (only when deal is in Lost stage) */}
            {fitScore?.deal_stage === 'lost' && (
              <LossDetailsCard
                fitScore={fitScore}
                onEdit={() => setEditingLoss(true)}
              />
            )}

            {/* ROW 2 — Key Metrics */}
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 md:p-6">
              <button
                type="button"
                onClick={() => toggleSection('keyMetrics')}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <div className="flex items-baseline gap-3 flex-wrap min-w-0">
                  <h2 className="text-white font-semibold text-lg">Key Metrics</h2>
                  {!sectionsOpen.keyMetrics && (
                    <span className="text-gray-500 text-sm">{metrics.length} metric{metrics.length !== 1 ? 's' : ''} tracked</span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {sectionsOpen.keyMetrics && (
                    <span className="text-gray-500 text-xs">{measuredCount}/{metrics.length} measured against target</span>
                  )}
                  <span className={`text-gray-500 text-xs transition-transform duration-200 ${sectionsOpen.keyMetrics ? 'rotate-90' : ''}`}>▶</span>
                </div>
              </button>
              {sectionsOpen.keyMetrics && (
                <div className="mt-4">
                  {metrics.length === 0 ? (
                    <p className="text-gray-500 text-sm">No quantitative metrics available on this property.</p>
                  ) : (
                    <div className="space-y-3">
                      {metrics.map((m, i) => {
                        const s = metricStatus(m);
                        return (
                          <div key={i} className="flex flex-col md:flex-row md:items-center gap-2">
                            <div className="md:w-40 text-gray-300 text-sm font-medium shrink-0">{m.label}</div>
                            <div className="flex-1">
                              <div className="w-full bg-[#0A0E27] rounded h-3 overflow-hidden">
                                <div className={`h-3 rounded transition-all ${s.color}`} style={{ width: `${Math.min(100, s.pct)}%` }}></div>
                              </div>
                            </div>
                            <div className="md:w-56 md:text-right text-sm shrink-0">
                              <div className="text-white font-semibold">{m.value}</div>
                              {m.targetLabel && (
                                <div className="text-gray-500 text-xs">Target: {m.targetLabel}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* When no grades exist yet, collapse Category Scores / Strengths / Concerns
                into a single helper banner. Weight Distribution is grade-independent
                (it's just the criteria hierarchy) so we still show that. */}
            {!hasGrades ? (
              <>
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#FFC107] p-5 md:p-6">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl leading-none">⏳</span>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold">Scoring in progress…</h3>
                      <p className="text-gray-400 text-sm mt-1">
                        This property hasn't been scored against this client's criteria yet. Category
                        scores, top strengths, and areas of concern will appear here once grades are
                        calculated.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {/* ROW 3 — Category Scores + Weight Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Category Scores — only when there are grades to summarize */}
              {hasGrades && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 md:p-6">
                <button
                  type="button"
                  onClick={() => toggleSection('categoryScores')}
                  className="w-full flex items-center justify-between gap-3 text-left"
                >
                  <div className="flex items-baseline gap-3 flex-wrap min-w-0">
                    <h2 className="text-white font-semibold text-lg">Category Scores</h2>
                    {!sectionsOpen.categoryScores && (
                      <span className="text-gray-500 text-sm">
                        {fitScore
                          ? <>{getScorePercent(fitScore.overall)}% <span className={getTierColor(fitScore.tier)}>{fitScore.tier}</span></>
                          : 'Not scored'}
                      </span>
                    )}
                  </div>
                  <span className={`text-gray-500 text-xs transition-transform duration-200 ${sectionsOpen.categoryScores ? 'rotate-90' : ''}`}>▶</span>
                </button>
                {sectionsOpen.categoryScores && (
                  <div className="mt-4">
                    {dimEntries.length === 0 ? (
                      <p className="text-gray-500 text-sm">No category scores yet. Score some criteria to see this breakdown.</p>
                    ) : (
                      <div className="space-y-3">
                        {dimEntries.map(([name, score]) => {
                          const pct = (score / 9) * 100;
                          return (
                            <div key={name}>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-gray-300 text-sm">{name}</span>
                                <span className="text-white text-sm font-semibold">{Number(score).toFixed(1)}/9</span>
                              </div>
                              <div className="w-full bg-[#0A0E27] rounded h-2.5 overflow-hidden">
                                <div className={`h-2.5 rounded ${scoreBarBg(score)} transition-all`} style={{ width: `${pct}%` }}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Weight Distribution Pie — independent of grades, kept visible */}
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 md:p-6">
                <button
                  type="button"
                  onClick={() => toggleSection('weightDist')}
                  className="w-full flex items-center justify-between gap-3 text-left"
                >
                  <div className="flex items-baseline gap-3 flex-wrap min-w-0">
                    <h2 className="text-white font-semibold text-lg">Criteria Weight Distribution</h2>
                    {!sectionsOpen.weightDist && (
                      <span className="text-gray-500 text-sm">{parentWeights.length} categor{parentWeights.length === 1 ? 'y' : 'ies'} weighted</span>
                    )}
                  </div>
                  <span className={`text-gray-500 text-xs transition-transform duration-200 ${sectionsOpen.weightDist ? 'rotate-90' : ''}`}>▶</span>
                </button>
                {sectionsOpen.weightDist && (
                  <div className="mt-4">
                    {parentWeights.length === 0 ? (
                      <p className="text-gray-500 text-sm">No criteria weights configured.</p>
                    ) : (
                      <div className="flex flex-col md:flex-row items-center gap-6">
                        <svg viewBox="0 0 200 200" className="w-48 h-48 shrink-0">
                          {pieSlices.map((s, i) => (
                            <g key={i}>
                              <path d={s.path} fill={s.color} stroke="#0A0E27" strokeWidth="1.5" />
                              <text x={s.labelX} y={s.labelY} fill="#0A0E27" fontSize="11" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                                {s.label}
                              </text>
                            </g>
                          ))}
                          <circle cx="100" cy="100" r="28" fill="#1A2744" stroke="#546E7A" strokeOpacity="0.3" />
                        </svg>
                        <div className="flex-1 space-y-2 text-sm w-full">
                          {parentWeights.map((p, i) => (
                            <div key={i} className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-3 h-3 rounded shrink-0" style={{ background: p.color }}></span>
                                <span className="text-gray-300 truncate">{p.name}</span>
                              </div>
                              <span className="text-white font-medium shrink-0">{fmtPct((p.weight / weightTotal) * 100)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ROW 4 — Strengths + Concerns (two cards, stack on mobile).
                Skipped when there are no grades; the banner above covers it. */}
            {hasGrades && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Top Strengths */}
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00E676] p-5">
                <button
                  type="button"
                  onClick={() => toggleSection('topStrengths')}
                  className="w-full flex items-center justify-between gap-2 text-left"
                >
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold">Top Strengths</h3>
                    {!sectionsOpen.topStrengths && (
                      <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {topStrengths.length > 0
                          ? <>{topStrengths[0].name} <span className="text-[#00E676] font-bold">{topStrengths[0].score.toFixed(1)}/9</span></>
                          : 'No grades yet'}
                      </div>
                    )}
                  </div>
                  <span className={`text-gray-500 text-xs transition-transform duration-200 ${sectionsOpen.topStrengths ? 'rotate-90' : ''}`}>▶</span>
                </button>
                {sectionsOpen.topStrengths && (
                  <div className="mt-3">
                    {topStrengths.length > 0 ? (
                      <div className="space-y-2">
                        {topStrengths.map((s, i) => (
                          <div key={i} className="flex justify-between items-center text-sm">
                            <span className="text-gray-300 truncate pr-2">{s.name}</span>
                            <span className="text-[#00E676] font-bold shrink-0">{s.score.toFixed(1)}/9</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No grades to display yet.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Areas to Consider */}
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#FF6B35] p-5">
                <button
                  type="button"
                  onClick={() => toggleSection('areasConcern')}
                  className="w-full flex items-center justify-between gap-2 text-left"
                >
                  <div className="min-w-0">
                    <h3 className="text-white font-semibold">Areas to Consider</h3>
                    {!sectionsOpen.areasConcern && (
                      <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {topConcerns.length > 0
                          ? <>{topConcerns[0].name} <span className={`font-bold ${topConcerns[0].score < 3 ? 'text-red-400' : 'text-orange-400'}`}>{topConcerns[0].score.toFixed(1)}/9</span></>
                          : 'No grades yet'}
                      </div>
                    )}
                  </div>
                  <span className={`text-gray-500 text-xs transition-transform duration-200 ${sectionsOpen.areasConcern ? 'rotate-90' : ''}`}>▶</span>
                </button>
                {sectionsOpen.areasConcern && (
                  <div className="mt-3">
                    {topConcerns.length > 0 ? (
                      <div className="space-y-2">
                        {topConcerns.map((c, i) => (
                          <div key={i} className="flex justify-between items-center text-sm">
                            <span className="text-gray-300 truncate pr-2">{c.name}</span>
                            <span className={`font-bold shrink-0 ${c.score < 3 ? 'text-red-400' : 'text-orange-400'}`}>{c.score.toFixed(1)}/9</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No grades to display yet.</p>
                    )}
                  </div>
                )}
              </div>

            </div>
            )}
          </div>
        )}

        {/* ──────────────── GRADING TAB ──────────────── */}
        {activeTab === 'grading' && (
          <div className="space-y-4">
            {user?.role === 'buyer' && (
              <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/20 p-4">
                <p className="text-gray-300 text-sm">
                  <span className="text-[#00D4FF] font-medium">Auto-Scored Grades</span> — These scores were calculated from listing data against your criteria.
                  After visiting a property, drag any slider to adjust the score based on your personal experience. Your overrides turn green. Click Reset to revert.
                </p>
              </div>
            )}

            {progress && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Scoring Progress</span>
                    <span className="text-white font-medium">{progress.graded}/{progress.total} ({progress.percentage}%)</span>
                  </div>
                  <div className="w-full bg-[#546E7A]/30 rounded-full h-2">
                    <div className="bg-[#00D4FF] h-2 rounded-full transition-all" style={{ width: `${progress.percentage}%` }}></div>
                  </div>
                </div>
                {progress.is_complete && <span className="text-[#00E676] text-sm font-medium">Complete!</span>}
              </div>
            )}

            {/* Expand / collapse all parent categories */}
            {hierarchy.length > 0 && (
              <div className="flex justify-end -mb-2">
                <button
                  type="button"
                  onClick={() => setAllGradingSections(!allGradingExpanded)}
                  className="text-[#00D4FF] hover:text-[#00E676] text-xs font-medium transition-colors"
                >
                  {allGradingExpanded ? 'Collapse All ▲' : 'Expand All ▼'}
                </button>
              </div>
            )}

            {hierarchy.map(parent => {
              const subs = parent.sub_criteria || [];
              // Compute per-category summary for the collapsed header.
              let gradedCount = 0;
              let scoreSum = 0;
              subs.forEach(sub => {
                const g = grades.find(gr => gr.criterion_id === sub.id);
                const o = overrides[sub.id];
                const score = o ? o.grade : (g ? g.grade : null);
                if (score != null) {
                  gradedCount += 1;
                  scoreSum += score;
                }
              });
              // Prefer the weighted dimension score from property_fit_scores when available
              // (it already accounts for sub-criterion weights); fall back to a simple average.
              const dimScore =
                fitScore && fitScore.dimension_scores && fitScore.dimension_scores[parent.name];
              const summaryScore =
                dimScore != null ? Number(dimScore) :
                gradedCount > 0 ? scoreSum / gradedCount : null;
              const isOpen = !!gradingSectionsOpen[parent.id];

              return (
              <div key={parent.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleGradingSection(parent.id)}
                  className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[#243B55]/30 transition-colors"
                >
                  <div className="flex items-baseline gap-3 flex-wrap min-w-0">
                    <h3 className="text-white font-semibold">{parent.name}</h3>
                    <span className="text-gray-500 text-sm font-normal">
                      ({(parent.weight * 100).toFixed(0)}% weight)
                    </span>
                    {!isOpen && (
                      <span className="text-gray-500 text-sm">
                        {summaryScore != null ? (
                          <span className="text-white font-medium">{summaryScore.toFixed(1)}/9</span>
                        ) : (
                          <span className="text-gray-600">Not graded</span>
                        )}
                        <span className="text-gray-600 mx-1">·</span>
                        <span>{gradedCount} of {subs.length} graded</span>
                      </span>
                    )}
                  </div>
                  <span className={`text-gray-500 text-xs transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                </button>
                {isOpen && (
                <div className="divide-y divide-[#546E7A]/10 border-t border-[#546E7A]/20">
                  {subs.map(sub => {
                    const existing = getExistingGrade(sub.id);
                    const override = overrides[sub.id];
                    const autoPercent = existing ? Math.round((existing / 9) * 100) : null;
                    const overridePercent = override ? Math.round((override.grade / 9) * 100) : null;
                    return (
                      <div key={sub.id} className="px-4 py-3">
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <span className="text-gray-200 text-sm font-medium">{sub.name}</span>
                            <span className="text-gray-500 text-xs ml-2">({(sub.weight * 100).toFixed(0)}%)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {autoPercent !== null && (
                              <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${override ? 'bg-gray-600 line-through' : getGradeColor(existing)}`}>
                                Auto: {autoPercent}%
                              </span>
                            )}
                            {override && (
                              <span className="px-2 py-0.5 rounded text-xs font-bold text-white bg-green-600">
                                Yours: {overridePercent}%
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs mb-2">{sub.description}</p>

                        {(user?.role === 'super_admin' || user?.role === 'broker' || user?.role === 'agent') && (
                          <div className="flex gap-1.5 flex-wrap">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(grade => (
                              <button
                                key={grade}
                                onClick={() => handleGrade(sub.id, grade)}
                                className={`w-9 h-8 rounded text-xs font-bold transition-all ${
                                  existing === grade
                                    ? `${getGradeColor(grade)} text-white ring-2 ring-white/50`
                                    : 'bg-[#0A0E27] text-gray-400 hover:bg-[#243B55] hover:text-white'
                                }`}
                              >
                                {grade}
                              </button>
                            ))}
                          </div>
                        )}

                        {user?.role === 'buyer' && (
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-6">0%</span>
                            <input type="range" min="1" max="9" step="1"
                              value={override ? override.grade : (existing || 5)}
                              onChange={(e) => handleOverride(sub.id, parseInt(e.target.value))}
                              className={`flex-1 h-2 ${override ? 'accent-[#00E676]' : 'accent-[#00D4FF]'}`} />
                            <span className={`text-sm font-bold w-12 text-right ${override ? 'text-[#00E676]' : 'text-[#00D4FF]'}`}>
                              {Math.round(((override ? override.grade : (existing || 5)) / 9) * 100)}%
                            </span>
                            {override && (
                              <button
                                onClick={() => removeOverride(sub.id)}
                                className="px-2 py-1 text-xs text-gray-500 hover:text-red-400 transition-colors whitespace-nowrap"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {/* ──────────────── NOTES TAB ──────────────── */}
        {activeTab === 'notes' && (() => {
          const ROLE_LABEL = {
            buyer: 'Client',
            agent: 'Agent',
            broker: 'Broker',
            super_admin: 'Admin',
          };
          const ROLE_COLOR = {
            buyer: 'text-[#00D4FF]',
            agent: 'text-[#00E676]',
            broker: 'text-purple-300',
            super_admin: 'text-orange-300',
          };
          const formatTimestamp = (iso) => {
            if (!iso) return '';
            const d = new Date(String(iso).replace(' ', 'T'));
            if (isNaN(d.getTime())) return '';
            return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
          };
          const canDelete = (note) => {
            if (!user) return false;
            if (user.role === 'super_admin') return true;
            // Note's creator may delete.
            if (note.created_by_user_id != null && note.created_by_user_id === user.id) return true;
            // Legacy fallback for notes created before the author column existed:
            // let the client whose profile they belong to manage them.
            if (note.created_by_user_id == null && user.role === 'buyer' && user.buyer_id === note.buyer_id) return true;
            return false;
          };
          const noteAuthor = (note) => {
            // Prefer the stored author name + role. Fall back to "the client" for legacy notes
            // (which were always client-authored before the role expansion).
            if (note.created_by_name) {
              return {
                name: note.created_by_name,
                role: ROLE_LABEL[note.created_by_role] || 'Team',
                roleKey: note.created_by_role,
              };
            }
            return { name: buyer?.name || 'Client', role: 'Client', roleKey: 'buyer' };
          };
          const heading = `Notes on this Property`;
          return (
            <div className="space-y-4">
              {/* Add-note card — visible to all four deal-team roles */}
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00E676] p-4 md:p-6">
                <h3 className="text-white font-semibold mb-1">Add a Note</h3>
                <p className="text-gray-500 text-xs mb-3">
                  {user?.role === 'buyer'
                    ? 'How do you feel about this property? Your notes help the AI write a more personal report.'
                    : 'Add an observation from a tour, call, or due diligence. Visible to the client and used by the AI report.'}
                </p>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add your observations about this property..."
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] resize-y"
                  rows={4}
                />
                <div className="flex justify-end mt-3">
                  <button
                    onClick={addNote}
                    disabled={savingNote || !newNote.trim()}
                    className="px-5 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors text-sm"
                  >
                    {savingNote ? 'Adding…' : 'Add Note'}
                  </button>
                </div>
              </div>

              {/* Notes list */}
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-white font-semibold">{heading}</h3>
                  <span className="text-gray-600 text-xs">{notes.length} note{notes.length !== 1 ? 's' : ''}</span>
                </div>

                {notes.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-6">
                    No notes yet. Add the first observation above.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {notes.map(note => {
                      const author = noteAuthor(note);
                      const colorCls = ROLE_COLOR[author.roleKey] || 'text-gray-300';
                      return (
                        <div key={note.id} className="bg-[#0A0E27]/50 rounded-lg p-3 border border-[#546E7A]/15">
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-200 text-sm whitespace-pre-wrap leading-relaxed">{note.note_text}</p>
                              <div className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500 mt-2">
                                <span className={colorCls}>
                                  <span className="font-medium">{author.name}</span>
                                  <span className="text-gray-500"> ({author.role})</span>
                                </span>
                                <span>·</span>
                                <span>{formatTimestamp(note.created_at)}</span>
                                {note.updated_at && note.updated_at !== note.created_at && (
                                  <>
                                    <span>·</span>
                                    <span className="italic">edited {formatTimestamp(note.updated_at)}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            {canDelete(note) && (
                              <button
                                onClick={() => { if (window.confirm('Delete this note?')) deleteNote(note.id); }}
                                className="text-gray-600 hover:text-red-400 text-sm shrink-0 transition-colors px-2"
                                title="Delete note"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ──────────────── AI REPORT TAB ──────────────── */}
        {activeTab === 'report' && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
              <h3 className="text-white font-semibold">AI Property Evaluation Report</h3>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={generateAiReport}
                  disabled={generatingReport}
                  className="px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] disabled:opacity-50 transition-colors text-sm"
                >
                  {generatingReport ? 'Generating...' : aiReport ? 'Regenerate Report' : 'Generate AI Report'}
                </button>
                {aiReport && (
                  <button
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`
                        <html>
                        <head>
                          <title>${property?.address} - Property Evaluation Report</title>
                          <style>
                            body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #1a1a1a; }
                            .header { border-bottom: 2px solid #00BCD4; padding-bottom: 16px; margin-bottom: 24px; }
                            .header .label { font-size: 11px; color: #888; letter-spacing: 2px; text-transform: uppercase; }
                            .header h1 { font-size: 24px; margin: 4px 0; }
                            .header .details { color: #555; font-size: 14px; }
                            .header .stats { margin-top: 8px; font-size: 13px; color: #00838F; }
                            .header .score { font-size: 28px; font-weight: bold; color: #00838F; margin-top: 8px; }
                            .section-header { font-size: 13px; font-weight: bold; color: #00838F; letter-spacing: 1px; border-bottom: 1px solid #00BCD4; padding-bottom: 4px; margin-top: 28px; margin-bottom: 12px; }
                            p { font-size: 14px; line-height: 1.7; color: #333; margin-bottom: 12px; }
                            .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; font-size: 11px; color: #999; text-align: center; }
                            @media print { body { padding: 20px; } }
                          </style>
                        </head>
                        <body>
                          <div class="header">
                            <div class="label">SchemeSync CRE — Property Evaluation</div>
                            <h1>${property?.address || ''}</h1>
                            <div class="details">${property?.city || ''}, ${property?.state || ''} ${property?.zip || ''}</div>
                            <div class="stats">${property?.sqft?.toLocaleString() || ''} sqft · $${property?.price ? Number(property.price).toLocaleString() : 'N/A'}</div>
                            <div class="score">${fitScore ? Math.round((fitScore.overall / 9) * 100) + '% — ' + fitScore.tier : ''}</div>
                          </div>
                          ${aiReport.split('\n').map(line => {
                            const trimmed = line.trim();
                            if (!trimmed) return '';
                            if (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && !trimmed.startsWith('$'))
                              return '<div class="section-header">' + trimmed + '</div>';
                            return '<p>' + line + '</p>';
                          }).join('')}
                          <div class="footer">Generated by SchemeSync CRE — AiDA Advisory LLC — ${new Date().toLocaleDateString()}</div>
                        </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                    className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] transition-colors text-sm"
                  >
                    Print Report
                  </button>
                )}
              </div>
            </div>
            {generatingReport ? (
              <div className="text-center py-12">
                <div className="text-[#00D4FF] text-lg mb-2">Generating AI evaluation...</div>
                <div className="text-gray-500 text-sm">Analyzing property data against client criteria</div>
              </div>
            ) : aiReport ? (
              <div className="bg-[#0A0E27] rounded-lg overflow-hidden">
                <div className="p-4 md:p-6 border-b border-[#546E7A]/30">
                  <div className="text-[#B0BEC5] text-xs tracking-widest mb-1">SCHEMESYNC CRE — PROPERTY EVALUATION</div>
                  <div className="text-white text-xl md:text-2xl font-bold">{property?.address}</div>
                  <div className="text-gray-400 text-sm">{property?.city}, {property?.state} {property?.zip}</div>
                  <div className="flex flex-wrap gap-3 md:gap-6 mt-3 text-sm">
                    {property?.sqft && <span className="text-[#00D4FF]">{property.sqft.toLocaleString()} sqft</span>}
                    {property?.price && <span className="text-[#00E676] font-bold">${Number(property.price).toLocaleString()}</span>}
                  </div>
                  {fitScore && (
                    <div className="mt-3 flex items-center gap-3">
                      <span className="text-2xl md:text-3xl font-bold text-white">{getScorePercent(fitScore.overall)}%</span>
                      <span className={`text-sm font-semibold ${getTierColor(fitScore.tier)}`}>{fitScore.tier}</span>
                    </div>
                  )}
                </div>
                <div className="p-4 md:p-6">
                  {aiReport.split('\n').map((line, i) => {
                    const trimmed = line.trim();
                    if (!trimmed) return <div key={i} className="h-3" />;
                    if (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && !trimmed.startsWith('$') && !trimmed.startsWith('#')) {
                      return (
                        <div key={i} className="mt-6 mb-3 pb-1 border-b border-[#00D4FF]/30">
                          <span className="text-[#00D4FF] text-sm font-bold tracking-wide">{trimmed}</span>
                        </div>
                      );
                    }
                    return <p key={i} className="text-gray-300 text-sm leading-relaxed mb-3">{line}</p>;
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">Click "Generate AI Report" to create a professional property evaluation.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <LossReasonModal
        open={!!pendingLossStage || editingLoss}
        mode={editingLoss ? 'edit' : 'record'}
        deal={{
          property_address: property?.property_name || property?.address,
          buyer_name: buyer?.name,
        }}
        initialReason={editingLoss ? (fitScore?.loss_reason || '') : ''}
        initialNotes={editingLoss ? (fitScore?.loss_notes || '') : ''}
        onCancel={() => {
          setPendingLossStage(null);
          setEditingLoss(false);
        }}
        onConfirm={async ({ reason, notes }) => {
          try {
            await persistStage('lost', { loss_reason: reason, loss_notes: notes });
            // Refetch fit-score so the Loss Details card reflects new values
            setFitScore((prev) => prev ? { ...prev, loss_reason: reason, loss_notes: notes } : prev);
            setPendingLossStage(null);
            setEditingLoss(false);
          } catch (e) {
            throw e;
          }
        }}
      />

      {/* Negotiation Brief modal */}
      {showNegotiation && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 overflow-y-auto"
          onClick={() => setShowNegotiation(false)}
        >
          <div
            className="bg-[#0A0E27] rounded-xl border border-[#546E7A]/40 w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-[#546E7A]/30 bg-[#0A0E27] rounded-t-xl">
              <h2 className="text-white text-lg font-semibold">Negotiation Brief</h2>
              <button
                onClick={() => setShowNegotiation(false)}
                className="text-gray-400 hover:text-white text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-5">
              {/* autoGenerate — clicking the orange button on the eval page
                  IS the explicit opt-in. The panel was patched so it correctly
                  shows the loading state (not a phantom "Regenerate" view)
                  while the first fetch runs. */}
              <NegotiationBriefPanel
                propertyId={propertyId}
                buyerId={buyerId}
                autoGenerate
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
